import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ContactModal from '../components/ContactModal';
import TestimonialSlider from '../components/TestimonialSlider';
import MobileSlider from '../components/MobileSlider';
import WhyYouNeedSlider from '../components/WhyYouNeedSlider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { FileText, CheckCircle, Shield, Lock, DollarSign, AlertCircle } from 'lucide-react';

const CopyrightRegistration = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const whyYouNeedReasons = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Secure your creative works with federal copyright protection and exclusive rights.',
    },
    {
      icon: Lock,
      title: 'Prevent Infringement',
      description: 'Stop unauthorized use, reproduction, or distribution of your creative content.',
    },
    {
      icon: DollarSign,
      title: 'Monetary Benefits',
      description: 'Eligible for statutory damages and attorney fees in infringement cases.',
    },
    {
      icon: AlertCircle,
      title: 'Public Record',
      description: 'Establish a public record of your copyright ownership and creation date.',
    },
  ];

  const steps = [
    {
      title: 'Document Review',
      description: 'We review your creative work and gather all necessary information to ensure proper copyright classification and registration.',
    },
    {
      title: 'Application Preparation',
      description: 'Our experts prepare your copyright application with accurate descriptions and proper categorization according to Copyright Office requirements.',
    },
    {
      title: 'Filing & Registration',
      description: 'We submit your application to the U.S. Copyright Office and monitor the process until your copyright certificate is issued.',
    },
  ];

  const packages = [
    {
      name: 'BASIC',
      price: '$99',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Document Review',
        'Application Preparation',
        'Copyright Filing',
        'Status Updates',
        'Certificate Delivery',
      ],
    },
    {
      name: 'STANDARD',
      price: '$199',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Document Review',
        'Application Preparation',
        'Copyright Filing',
        'Priority Processing',
        'Dedicated Case Manager',
        'Status Updates',
        'Certificate Delivery',
      ],
      popular: true,
    },
    {
      name: 'PREMIUM',
      price: '$299',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Document Review by Expert',
        'Application Preparation',
        'Copyright Filing',
        'Expedited Processing',
        'Legal Consultation',
        'Infringement Monitoring',
        'Certificate Delivery',
      ],
    },
  ];

  const testimonials = [
    {
      name: 'Alex Martinez',
      company: 'Martinez Photography',
      text: 'Fine Trademark handled my photography copyright registration perfectly. Quick, professional, and hassle-free!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=200&h=200&fit=crop',
    },
    {
      name: 'Lisa Chen',
      company: 'Creative Studios',
      text: 'Excellent service! They registered copyrights for all my creative works efficiently. Highly recommend their expertise.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1652471949169-9c587e8898cd?w=200&h=200&fit=crop',
    },
    {
      name: 'James Wilson',
      company: 'Wilson Music Productions',
      text: 'Protected my music compositions with their help. Professional team that understands the creative industry.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'What can be copyrighted?',
      answer: 'Original works of authorship including literary works, music, dramatic works, art, photographs, software, architecture, and other creative expressions can be copyrighted.',
    },
    {
      question: 'How long does copyright registration take?',
      answer: 'Standard processing typically takes 6-8 months. Expedited processing is available for faster registration, usually within 1-2 months.',
    },
    {
      question: 'Do I need to register my copyright?',
      answer: 'While copyright protection exists automatically upon creation, registration provides significant legal advantages including the ability to file infringement lawsuits and claim statutory damages.',
    },
    {
      question: 'How long does copyright protection last?',
      answer: 'For works created after 1978, copyright generally lasts for the life of the author plus 70 years. For works made for hire, it lasts 95 years from publication or 120 years from creation.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-[#E5B80B] text-[#1E1E1E] hover:bg-[#E5B80B] text-sm px-4 py-1 mb-6">
              Protect Your Creative Works
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6">
              Copyright Registration Services
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8">
              Secure legal protection for your creative works. Register your copyrights and establish your ownership rights.
            </p>
            <Button 
              size="lg" 
              className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105"
              onClick={() => setIsModalOpen(true)}
            >
              Register Your Copyright
            </Button>
          </div>
        </div>
      </section>

      {/* Why You Need Copyright Registration */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4">Why You Need Copyright Registration</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Protect your creative works with federal copyright registration
            </p>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden">
            <WhyYouNeedSlider reasons={whyYouNeedReasons} />
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-8">
            {whyYouNeedReasons.map((reason, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-6 bg-[#E5B80B]/10 rounded-full">
                    <reason.icon className="h-12 w-12 text-[#E5B80B]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1E1E1E] mb-4">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Mobile/Tablet - Image First */}
          <div className="lg:hidden space-y-8">
            <div className="relative h-64 sm:h-80 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800"
                alt="Copyright Registration"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1E1E]">About Copyright Registration</h2>
              <p className="text-base text-gray-600 leading-relaxed">
                Copyright registration provides legal documentation of your ownership and the creation date of your original work. It's essential for protecting your intellectual property and enforcing your rights.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                Our copyright registration services ensure your creative works are properly protected under federal law, giving you the legal foundation to defend against infringement.
              </p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1E1E]">About Copyright Registration</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Copyright registration provides legal documentation of your ownership and the creation date of your original work. It's essential for protecting your intellectual property and enforcing your rights.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our copyright registration services ensure your creative works are properly protected under federal law, giving you the legal foundation to defend against infringement.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800"
                alt="Copyright Registration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4">Our Registration Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Simple three-step process to register your copyright
            </p>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden pb-12">
            <MobileSlider items={steps} type="steps" />
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E1E1E] mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1E1E] mb-6">Transparent Pricing</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the package that fits your needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl scale-105' : 'border-2'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Button 
                    className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1E1E] mb-4">What Our Clients Say</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} showServiceBadge={false} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1E1E] mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-[#1E1E1E] hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-base text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default CopyrightRegistration;